import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiPlus, FiEdit2, FiToggleLeft, FiToggleRight, FiPauseCircle, FiCrosshair, FiClock, FiMove, FiCode } from 'react-icons/fi';
import PanelChart from './PanelChart';
import MergedPanelView from './MergedPanelView';
import SqlViewModal from './SqlViewModal';
import { extractBindNames } from './bindUtils';
import styles from './MonitoringDashboardModal.module.css';

/**
 * 패널 카드에서 "SQL 보기" 모달이 사용할 binds 계산.
 *
 * PanelTabContent 의 fetch 루프 (`computeBindsForPanel` + 백엔드 자동 주입) 와
 * 동등하게:
 *  - panel.consumes 의 globalVar → bindName 매핑
 *  - SQL 의 :name 이 tab variables 에 직접 있으면 폴백 매핑
 *  - SYSTEM_BINDS (minutes/hours) 자동 주입
 *
 * 이 함수의 결과를 SqlViewModal 의 binds 로 넘기면 substituteBinds 가 치환.
 */
function computeViewBinds(panel, variables, timeWindowMin) {
  const out = {};
  const vars = variables || {};
  for (const c of (panel?.consumes || [])) {
    if (c?.bindName && c?.globalVar) {
      out[c.bindName] = vars[c.globalVar];
    }
  }
  // 자동 주입 — 백엔드 가 같은 방식으로 :minutes / :hours 를 채움.
  // hours 는 정수로 (Math.ceil) 해야 INTERVAL ':hours hours' 류 표현이 깨지지 않음.
  if (typeof timeWindowMin === 'number' && timeWindowMin > 0) {
    out.minutes = timeWindowMin;
    out.hours = Math.max(1, Math.ceil(timeWindowMin / 60));
  }
  // 폴백 — SQL 에 있는 바인드 중 위에서 못 채운 게 tab variables 에 있으면 매핑
  const required = extractBindNames(panel?.sql || '');
  for (const name of required) {
    if (out[name] === undefined && vars[name] !== undefined) {
      out[name] = vars[name];
    }
  }
  return out;
}

// Drag & Drop data transfer MIME. 다른 앱으로부터의 드래그는 무시하기 위해 고유 타입 사용.
const DND_TYPE = 'application/x-livesql-panel';

/**
 * 패널을 탭의 `layout`에 맞춰 고정 슬롯 그리드로 렌더.
 *
 * layout 값:
 *   - "1x1" / "1x2" / "2x1" / "2x2" → 지정 레이아웃의 고정 슬롯 그리드.
 *   - 그 외 (undefined / null / 기타) → 기존 auto-fit 그리드로 폴백.
 *
 * 슬롯 매핑:
 *   - 각 패널의 `slotIndex` (0-based) 를 그대로 슬롯 위치로 사용.
 *   - slotIndex 없는 패널은 렌더 시점에 0부터 빈 자리 채우기로 가상 할당(저장 X).
 *   - slotIndex 가 그리드 범위(slotCount) 를 초과하면 "숨겨진 패널" 로 분류하고
 *     그리드 아래에 안내 배너 표시.
 */

// 고정 그리드 레이아웃 → 슬롯 수 매핑
// 대칭: 1x1(1), 1x2(2·가로), 2x1(2·세로), 2x2(4)
// 비대칭: 1L2R(3·좌측 큰 패널 + 우측 2분할), 2L1R(3·좌측 2분할 + 우측 큰 패널)
const SLOT_COUNT = { '1x1': 1, '1x2': 2, '2x1': 2, '2x2': 4, '1L2R': 3, '2L1R': 3 };
const GRID_CLASS = {
  '1x1': styles.slotGrid1x1,
  '1x2': styles.slotGrid1x2,
  '2x1': styles.slotGrid2x1,
  '2x2': styles.slotGrid2x2,
  '1L2R': styles.slotGrid1L2R,
  '2L1R': styles.slotGrid2L1R,
};

function PanelCard({
  panel, panelState, onEdit, onToggle, onElementClick, isDarkMode,
  isDragging, dragHandlers, variables, isFullscreen,
  panelById, panelData, timeWindowMin, timeDomain, displayMode,
}) {
  const { t } = useTranslation();
  // legacy 패널 (enabled 필드 없음) 은 활성으로 간주
  const isEnabled = panel.enabled !== false;
  const isPublisher = !!(panel.produces && panel.produces.enabled && panel.produces.name);
  // SQL 보기 모달 — 바인드 치환된 실제 실행 SQL 표시.
  // merged 패널은 자체 SQL 이 없으므로 버튼 자체를 hide.
  const [sqlViewOpen, setSqlViewOpen] = useState(false);
  const hasSqlButton = panel.chartType !== 'merged';
  // 현재 발행 변수의 값 (없으면 표시 생략)
  const currentVarValue = isPublisher && variables ? variables[panel.produces.name] : undefined;
  const formatVarVal = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const s = String(v);
    return s.length > 20 ? s.slice(0, 18) + '…' : s;
  };
  const formattedVarValue = formatVarVal(currentVarValue);
  const waiting = panelState?.waiting;
  const titleAlign = panel.titleAlign || 'left';
  const titleJustify =
      titleAlign === 'center' ? 'center'
    : titleAlign === 'right'  ? 'flex-end'
    :                            'flex-start';
  return (
    <div
      className={`${styles.chartSection} ${!isEnabled ? styles.chartSectionDisabled : ''} ${isDragging ? styles.chartSectionDragging : ''}`}
      {...(dragHandlers?.cardProps || {})}
    >
      <div className={styles.panelHeader}>
        {/* 드래그 핸들 — 카드 전체를 draggable 하게 하되 텍스트 선택과 충돌 방지 위해 명시적 핸들 제공 */}
        {dragHandlers && (
          <span
            className={styles.panelDragHandle}
            title={t('monitoring:slot.dragReorder')}
            draggable
            onDragStart={dragHandlers.onHandleDragStart}
            onDragEnd={dragHandlers.onHandleDragEnd}
          >
            <FiMove size={12} />
          </span>
        )}
        {!panel.hideTitle && (
          <div className={styles.panelTitleWrapper} style={{ justifyContent: titleJustify }}>
            <h3>{panel.title}</h3>
            {isPublisher && (
              <span
                className={styles.panelPublisherBadge}
                title={t('monitoring:slot.producesVarFull', { name: panel.produces.name, extract: panel.produces.extract })
                  + (formattedVarValue !== null ? t('monitoring:slot.currentValue', { value: currentVarValue }) : '')}
              >
                <FiCrosshair size={11} style={{ marginRight: 3 }} />
                {panel.produces.name}
                {formattedVarValue !== null && (
                  <span className={styles.panelPublisherBadgeValue}> = {formattedVarValue}</span>
                )}
              </span>
            )}
          </div>
        )}
        {panel.hideTitle && isPublisher && (
          <span
            className={styles.panelPublisherBadge}
            title={t('monitoring:slot.producesVarFull', { name: panel.produces.name, extract: panel.produces.extract })
                  + (formattedVarValue !== null ? t('monitoring:slot.currentValue', { value: currentVarValue }) : '')}
          >
            <FiCrosshair size={11} style={{ marginRight: 3 }} />
            {panel.produces.name}
            {formattedVarValue !== null && (
              <span className={styles.panelPublisherBadgeValue}> = {formattedVarValue}</span>
            )}
          </span>
        )}
        {panel.hideTitle && <div style={{ flex: 1 }} />}
        {/* 고칠 수 없는 대시보드(구독 등)는 처리기가 없다 — 단추를 그리지 않는다 */}
        {onToggle && (
          <button
            className={`${styles.panelToggleBtn} ${isEnabled ? styles.panelToggleBtnOn : ''}`}
            onClick={() => onToggle(panel)}
            title={isEnabled ? t('monitoring:slot.disable') : t('monitoring:slot.enable')}
          >
            {isEnabled ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
          </button>
        )}
        {onEdit && (
          <button className={styles.panelEditBtn} onClick={onEdit} title={t('monitoring:slot.edit')}>
            <FiEdit2 size={12} />
          </button>
        )}
        {hasSqlButton && (
          <button
            className={styles.panelEditBtn}
            onClick={() => setSqlViewOpen(true)}
            title={t('monitoring:slot.viewSql')}
          >
            <FiCode size={12} />
          </button>
        )}
      </div>
      {sqlViewOpen && (
        <SqlViewModal
          panel={panel}
          binds={computeViewBinds(panel, variables, timeWindowMin)}
          onClose={() => setSqlViewOpen(false)}
        />
      )}
      {!isEnabled ? (
        <div className={styles.panelDisabledBody}>
          <FiPauseCircle size={20} />
          <span>{t('monitoring:slot.inactiveHint')}</span>
        </div>
      ) : Array.isArray(waiting) && waiting.length > 0 ? (
        <div className={styles.panelWaitingBody}>
          <FiClock size={20} />
          <span>{t('monitoring:slot.awaitingSelection')}</span>
          <small>{t('monitoring:slot.neededVarsList', { vars: waiting.map(v => `:${v}`).join(', ') })}</small>
        </div>
      ) : (
        <div className={styles.panelChartWrapper}>
          {panelState?.error && (
            <div className={styles.panelError}>{t('monitoring:slot.queryErrorMsg', { message: panelState.error })}</div>
          )}
          {panel.chartType === 'merged' ? (
            <MergedPanelView
              panel={panel}
              panelById={panelById}
              panelData={panelData}
              isDarkMode={isDarkMode}
              isFullscreen={isFullscreen}
              timeWindowMin={timeWindowMin}
              timeDomain={timeDomain}
              displayMode={displayMode}
            />
          ) : (
            <PanelChart
              panel={panel}
              data={panelState?.data || []}
              height="auto"
              isDarkMode={isDarkMode}
              isFullscreen={isFullscreen}
              timeWindowMin={timeWindowMin}
              timeDomain={timeDomain}
              displayMode={displayMode}
              onElementClick={isPublisher ? ((datum) => onElementClick && onElementClick(panel, datum)) : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

function EmptySlot({ dropProps, isDropActive, onAdd }) {
  const { t } = useTranslation();
  // 두 가지 추가 경로 모두 지원:
  //   1) 클릭 → onAdd 가 PanelEditor 를 해당 슬롯 위치로 열어 신규 패널 추가
  //   2) 하단 collapsed strip 에서 드래그 → 이 영역에 drop
  return (
    <div
      className={`${styles.emptySlot} ${isDropActive ? styles.emptySlotDropActive : ''}`}
      onClick={onAdd}
      role={onAdd ? 'button' : undefined}
      tabIndex={onAdd ? 0 : undefined}
      onKeyDown={(e) => {
        if (!onAdd) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onAdd(); }
      }}
      title={onAdd ? t('monitoring:slot.clickToAdd') : undefined}
      {...(dropProps || {})}
    >
      {/* 추가할 수 없으면(순환 중 · 편집 권한 없음) 빈 칸으로만 — 「+ 패널 추가」 를 보이고 눌러도 아무 일이 없으면 고장처럼 보인다 */}
      {onAdd && (
        <>
          <span className={styles.emptySlotIcon}>
            <FiPlus size={22} />
          </span>
          <span className={styles.emptySlotLabel}>{t('monitoring:slot.addPanel')}</span>
          <span className={styles.emptySlotHint}>{t('monitoring:slot.orDragBelow')}</span>
        </>
      )}
    </div>
  );
}

/**
 * 접기(collapsed) 패널 카드 — 화면을 차지하지 않는 작은 한 줄 카드.
 * 헤더(드래그 핸들 · 제목 · 발행 변수 배지 · 토글 · 편집) 만 표시. 차트는 미리 그리지 않음.
 * 드래그해서 그리드 슬롯으로 이동 가능 — swap 또는 빈 슬롯 채우기.
 */
function CollapsedCard({ panel, onEdit, onToggle, isDragging, dragHandlers, variables }) {
  const { t } = useTranslation();
  const isEnabled = panel.enabled !== false;
  const isPublisher = !!(panel.produces && panel.produces.enabled && panel.produces.name);
  const currentVarValue = isPublisher && variables ? variables[panel.produces.name] : undefined;
  const formatVarVal = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const s = String(v);
    return s.length > 20 ? s.slice(0, 18) + '…' : s;
  };
  const formattedVarValue = formatVarVal(currentVarValue);
  return (
    <div className={`${styles.collapsedCard} ${isDragging ? styles.collapsedCardDragging : ''} ${!isEnabled ? styles.collapsedCardDisabled : ''}`}>
      {dragHandlers && (
        <span
          className={styles.collapsedCardDragHandle}
          title={t('monitoring:slot.dragToSlot')}
          draggable
          onDragStart={dragHandlers.onHandleDragStart}
          onDragEnd={dragHandlers.onHandleDragEnd}
        >
          <FiMove size={11} />
        </span>
      )}
      <span className={styles.collapsedCardTitle} title={panel.title || t('monitoring:slot.noTitle')}>
        {panel.title || <em>{t('monitoring:slot.noTitle')}</em>}
      </span>
      {isPublisher && (
        <span className={styles.panelPublisherBadge} title={t('monitoring:slot.producesVar', { name: panel.produces.name })}>
          <FiCrosshair size={10} style={{ marginRight: 2 }} />
          {panel.produces.name}
          {formattedVarValue !== null && (
            <span className={styles.panelPublisherBadgeValue}> = {formattedVarValue}</span>
          )}
        </span>
      )}
      {onToggle && (
        <button
          className={`${styles.panelToggleBtn} ${isEnabled ? styles.panelToggleBtnOn : ''}`}
          onClick={() => onToggle(panel)}
          title={isEnabled ? t('monitoring:slot.inactive') : t('monitoring:slot.active')}
        >
          {isEnabled ? <FiToggleRight size={14} /> : <FiToggleLeft size={14} />}
        </button>
      )}
      {onEdit && (
        <button className={styles.panelEditBtn} onClick={onEdit} title={t('monitoring:slot.edit')}>
          <FiEdit2 size={11} />
        </button>
      )}
    </div>
  );
}

// 고정 슬롯의 드롭 수용 wrapper — EmptySlot 은 button 이라 drop events 가 약간 다름,
// PanelCard 에는 drop zone 이 필요하므로 outer div 로 감쌈.
function SlotDropZone({ slotIndex, isDropActive, onDragOver, onDragEnter, onDragLeave, onDrop, children }) {
  return (
    <div
      data-slot={slotIndex}
      className={`${styles.slotCell} ${isDropActive ? styles.slotCellDropActive : ''}`}
      onDragOver={onDragOver}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
    </div>
  );
}

const PanelSlotGrid = ({
  tabId,
  layout,
  panels,
  panelData,
  panelById,
  timeWindowMin,
  timeDomain,
  displayMode,
  onEditPanel,
  onAddPanelAtSlot,
  onAddPanelAppend,
  onTogglePanel,
  onPanelElementClick,
  onReorderPanel,
  variables,
  isFullscreen,
  isDarkMode,
  // 순환 중(2026-10-18) — 패널만. 접힌 패널 줄·추가 줄을 그리지 않는다
  presentation = false,
}) => {
  const { t } = useTranslation();
  // 고정 레이아웃 여부
  const fixedSlotCount = SLOT_COUNT[layout];
  const isFixed = typeof fixedSlotCount === 'number';

  // 드래그 중인 panelId + 호버 중인 target slotIndex — 시각적 피드백 용
  const [draggingPanelId, setDraggingPanelId] = useState(null);
  const [dropTargetSlot, setDropTargetSlot] = useState(null);

  const makeDragHandlers = (panel) => {
    if (!onReorderPanel || !isFixed) return null;   // auto-fit 에서는 드래그 비활성
    return {
      cardProps: {
        // 카드 전체에 draggable 을 주면 차트 내부 상호작용과 충돌하므로 핸들에서만
      },
      onHandleDragStart: (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(DND_TYPE, panel.id);
        // 텍스트 fallback 도 세팅 — 일부 브라우저가 dataTransfer 비어있으면 drag 취소
        e.dataTransfer.setData('text/plain', panel.id);
        setDraggingPanelId(panel.id);
      },
      onHandleDragEnd: () => {
        setDraggingPanelId(null);
        setDropTargetSlot(null);
      },
    };
  };

  const makeDropProps = (targetSlotIndex) => {
    if (!onReorderPanel || !isFixed) return null;
    return {
      onDragOver: (e) => {
        if (!draggingPanelId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDragEnter: () => { if (draggingPanelId) setDropTargetSlot(targetSlotIndex); },
      onDragLeave: (e) => {
        // child 로의 이동은 leave 취급 안 함
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDropTargetSlot(prev => prev === targetSlotIndex ? null : prev);
      },
      onDrop: (e) => {
        e.preventDefault();
        const sourcePanelId = e.dataTransfer.getData(DND_TYPE) || e.dataTransfer.getData('text/plain');
        setDraggingPanelId(null);
        setDropTargetSlot(null);
        if (!sourcePanelId) return;
        onReorderPanel(tabId, sourcePanelId, targetSlotIndex);
      },
    };
  };

  // slotIndex 없는 패널에 가상 슬롯 부여 (렌더 시점 only).
  // 이미 점유된 슬롯은 건너뛰고 낮은 번호부터 채움.
  // slotIndex >= slotCount 인 패널 = "접기 (collapsed)" 상태 — 하단 strip 에 표시.
  const { slotByIndex, collapsedPanels } = useMemo(() => {
    const list = panels || [];
    if (!isFixed) {
      return { slotByIndex: null, collapsedPanels: [] };
    }
    const byIndex = new Map();
    const noIndexQueue = [];
    list.forEach(p => {
      if (typeof p.slotIndex === 'number' && p.slotIndex >= 0) {
        if (byIndex.has(p.slotIndex)) noIndexQueue.push(p);
        else byIndex.set(p.slotIndex, p);
      } else {
        noIndexQueue.push(p);
      }
    });
    // slotIndex 없는 패널은 가용 슬롯(빈 자리 + 그 너머의 collapsed 영역) 에 순서대로
    let nextIdx = 0;
    while (noIndexQueue.length > 0) {
      if (!byIndex.has(nextIdx)) byIndex.set(nextIdx, noIndexQueue.shift());
      nextIdx++;
      if (nextIdx > 200) break;   // 안전 가드
    }
    // 표시용 슬롯 맵 (0..slotCount-1)
    const inRange = new Map();
    for (let i = 0; i < fixedSlotCount; i++) {
      if (byIndex.has(i)) inRange.set(i, byIndex.get(i));
    }
    // collapsed = slotIndex >= slotCount, slotIndex 오름차순 정렬
    const collapsed = [];
    for (const [idx, panel] of byIndex.entries()) {
      if (idx >= fixedSlotCount) collapsed.push(panel);
    }
    collapsed.sort((a, b) => (a.slotIndex || 0) - (b.slotIndex || 0));
    return { slotByIndex: inRange, collapsedPanels: collapsed };
  }, [panels, isFixed, fixedSlotCount]);

  // 고정 레이아웃 렌더
  if (isFixed) {
    const cells = [];
    for (let i = 0; i < fixedSlotCount; i++) {
      const panel = slotByIndex.get(i);
      const dropProps = makeDropProps(i);
      const isDropActive = dropTargetSlot === i && draggingPanelId && (!panel || panel.id !== draggingPanelId);
      if (panel) {
        cells.push(
          <SlotDropZone key={`slot-${i}`} slotIndex={i} isDropActive={isDropActive} {...(dropProps || {})}>
            <PanelCard
              panel={panel}
              panelState={panelData?.[panel.id]}
              onEdit={onEditPanel ? () => onEditPanel(tabId, panel) : null}
              onToggle={onTogglePanel ? (p) => onTogglePanel(tabId, p) : null}
              onElementClick={(p, datum) => onPanelElementClick && onPanelElementClick(tabId, p, datum)}
              isDarkMode={isDarkMode}
              isDragging={draggingPanelId === panel.id}
              dragHandlers={makeDragHandlers(panel)}
              variables={variables}
              isFullscreen={isFullscreen}
              panelById={panelById}
              panelData={panelData}
              timeWindowMin={timeWindowMin}
              timeDomain={timeDomain}
              displayMode={displayMode}
            />
          </SlotDropZone>
        );
      } else {
        const slotIdx = i;
        cells.push(
          <SlotDropZone key={`slot-${i}`} slotIndex={i} isDropActive={isDropActive} {...(dropProps || {})}>
            <EmptySlot
              dropProps={null}
              isDropActive={isDropActive}
              onAdd={onAddPanelAtSlot ? () => onAddPanelAtSlot(tabId, slotIdx) : null}
            />
          </SlotDropZone>
        );
      }
    }

    // Collapsed strip drop handler — 그리드 패널을 strip 으로 끌어와 접기 처리
    const stripDropProps = onReorderPanel ? {
      onDragOver: (e) => {
        if (!draggingPanelId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      },
      onDragEnter: () => {
        if (draggingPanelId) setDropTargetSlot('strip');
      },
      onDragLeave: (e) => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setDropTargetSlot(prev => prev === 'strip' ? null : prev);
      },
      onDrop: (e) => {
        e.preventDefault();
        const sourcePanelId = e.dataTransfer.getData(DND_TYPE) || e.dataTransfer.getData('text/plain');
        setDraggingPanelId(null);
        setDropTargetSlot(null);
        if (!sourcePanelId) return;
        // 다음 collapsed slotIndex 계산 — 기존 모든 slotIndex 의 max+1, 단 slotCount 이상
        const allIdx = (panels || [])
          .filter(p => p.id !== sourcePanelId)
          .map(p => typeof p.slotIndex === 'number' ? p.slotIndex : -1);
        const maxIdx = allIdx.length > 0 ? Math.max(...allIdx) : -1;
        const nextCollapsedIdx = Math.max(fixedSlotCount, maxIdx + 1);
        onReorderPanel(tabId, sourcePanelId, nextCollapsedIdx);
      },
    } : {};

    return (
      <>
        <div className={`${styles.slotGrid} ${GRID_CLASS[layout]}`}>
          {cells}
        </div>
        {/* 하단 접기 strip — 편집할 때만(순환 중에는 패널만 보인다). 한 줄 고정 — 줄바꿈으로 길어지면 화면에 스크롤이 생긴다(2026-10-18).
            그리드 패널을 여기로 드래그하면 접기됨. */}
        {!presentation && (
        <div className={`${styles.collapsedStrip} ${dropTargetSlot === 'strip' ? styles.collapsedStripDropActive : ''}`} {...stripDropProps}>
          {onAddPanelAppend && (
            <button
              type="button"
              className={styles.collapsedAddBtn}
              onClick={() => onAddPanelAppend(tabId)}
              title={t('monitoring:slot.addCollapsed')}
            >
              <FiPlus size={14} />
              <span>{t('monitoring:slot.addPanel')}</span>
            </button>
          )}
          {collapsedPanels.length === 0 ? (
            <span className={styles.collapsedStripHint}>
              {t('monitoring:slot.stripHint')}
            </span>
          ) : (
            collapsedPanels.map(panel => (
              <CollapsedCard
                key={panel.id}
                panel={panel}
                onEdit={onEditPanel ? () => onEditPanel(tabId, panel) : null}
                onToggle={onTogglePanel ? (p) => onTogglePanel(tabId, p) : null}
                isDragging={draggingPanelId === panel.id}
                dragHandlers={makeDragHandlers(panel)}
                variables={variables}
              />
            ))
          )}
        </div>
        )}
      </>
    );
  }

  // 자동 배치 — 패널 수로 열·행을 정해 **모든 패널이 한 화면에** 들어오게 한다(2026-10-18 — 예전에는 행 높이가 350px 고정이라
  // 패널이 늘면 아래로 이어져 스크롤이 생겼다). 「패널 추가」 는 칸을 차지하지 않고 아래 얇은 줄로
  const hasPanels = panels && panels.length > 0;
  const n = hasPanels ? panels.length : 0;
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : n <= 9 ? 3 : 4;
  const rows = Math.max(1, Math.ceil(n / cols));
  return (
    <>
    <div className={styles.panelGrid} style={hasPanels ? { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` } : undefined}>
      {hasPanels ? panels.map(panel => (
        <PanelCard
          key={panel.id}
          panel={panel}
          panelState={panelData?.[panel.id]}
          onEdit={onEditPanel ? () => onEditPanel(tabId, panel) : null}
          onToggle={onTogglePanel ? (p) => onTogglePanel(tabId, p) : null}
          onElementClick={(p, datum) => onPanelElementClick && onPanelElementClick(tabId, p, datum)}
          isDarkMode={isDarkMode}
          variables={variables}
          isFullscreen={isFullscreen}
          panelById={panelById}
          panelData={panelData}
          timeWindowMin={timeWindowMin}
          timeDomain={timeDomain}
          displayMode={displayMode}
        />
      )) : (
        <div className={styles.emptyTab}>
          <p>{t('monitoring:slot.empty')}</p>
          {onAddPanelAppend && (
            <button className={styles.btnPrimary} onClick={() => onAddPanelAppend(tabId)}>
              <FiPlus size={14} style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {t('monitoring:slot.addPanel')}
            </button>
          )}
        </div>
      )}
    </div>
      {hasPanels && onAddPanelAppend && !presentation && (
        <div className={styles.addBar}>
          <button type="button" className={styles.collapsedAddBtn} onClick={() => onAddPanelAppend(tabId)} title={t('monitoring:slot.addPanel')}>
            <FiPlus size={14} />
            <span>{t('monitoring:slot.addPanel')}</span>
          </button>
        </div>
      )}
    </>
  );
};

export default PanelSlotGrid;
